import { useEffect, useRef, useState } from "react";
import axios, { API } from "../../api";
import { PhotoEditModal } from "./PhotoEditModal";
import { PhotoShareSheet } from "../PhotoShareSheet";

/**
 * Camera capture modal — request permission, live preview, snap a photo, send to Aura.
 * On send, calls POST /api/vision/analyze and shows Aura's reaction inline.
 *
 * Props:
 *   onClose() — close without sending
 *   onCaptured({ image_base64, reaction, mood, mood_emoji, mood_shifted }) — after Aura responds
 *   initialContext? — optional text to send along with the photo (from conversation)
 *   onMoodUpdate? — propagate mood change to parent
 */
export function CameraCaptureModal({ onClose, onCaptured, initialContext = "", onMoodUpdate, onPaintWallpaper, paintWallpaperLocked }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [step, setStep] = useState("idle"); // idle | live | review | sending | done
  const [error, setError] = useState("");
  const [snapshot, setSnapshot] = useState(null); // data URL
  const [context, setContext] = useState(initialContext);
  const [reaction, setReaction] = useState("");
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Phase 25.3 — Camera facing toggle (selfie ↔ back). Front is default for the
  // common Aura use-case (selfie reactions), back for "look at this in my world".
  const [facingMode, setFacingMode] = useState("user");

  useEffect(() => {
    startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  const startCamera = async () => {
    setError("");
    try {
      stopCamera();
      // Phase 29.K — pre-flight permission check for clearer error UX.
      try {
        const { ensureCameraPermission } = await import("../../lib/permissions");
        const ok = await ensureCameraPermission();
        if (!ok) {
          setError("Camera permission denied. Tap the 🔒 in your address bar → Permissions → Camera → Allow.");
          setStep("idle");
          return;
        }
      } catch (_) { /* permissions module unavailable — proceed */ }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStep("live");
    } catch (e) {
      console.error("camera error", e);
      setError("Camera permission denied or unavailable. Please allow camera access.");
      setStep("idle");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    // Cap output at 1024x768 for speed/cost
    const maxW = 1024;
    const ratio = video.videoWidth ? Math.min(1, maxW / video.videoWidth) : 1;
    canvas.width = Math.round(video.videoWidth * ratio);
    canvas.height = Math.round(video.videoHeight * ratio);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setSnapshot(dataUrl);
    setStep("review");
    setError(""); // Phase 30 — clear any prior camera-permission error once we have a valid snap
    stopCamera();
  };

  const retake = async () => {
    setSnapshot(null);
    setReaction("");
    await startCamera();
  };

  const send = async () => {
    if (!snapshot) return;
    setStep("sending");
    setError("");
    try {
      const base64 = snapshot.split(",")[1];
      const res = await axios.post(`${API}/vision/analyze`, {
        image_base64: base64,
        context: context.trim() || null
      });
      setReaction(res.data.reaction || "");
      setStep("done");
      if (onMoodUpdate && res.data.mood) {
        onMoodUpdate({ mood: res.data.mood, emoji: res.data.mood_emoji });
      }
      if (onCaptured) {
        onCaptured({
          image_base64: base64,
          reaction: res.data.reaction,
          mood: res.data.mood,
          mood_emoji: res.data.mood_emoji,
          mood_shifted: res.data.mood_shifted
        });
      }
    } catch (e) {
      console.error("vision send error", e);
      setError(e.response?.data?.detail || "Failed to send photo to Aura");
      setStep("review");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="camera-modal">
      <div className="camera-modal" onClick={(e) => e.stopPropagation()}>
        <div className="journal-header">
          <h2>📷 Show Aura</h2>
          <button className="journal-close" onClick={onClose} data-testid="camera-close-btn">×</button>
        </div>

        {error && <div className="error-message" data-testid="camera-error">{error}</div>}

        {(step === "live" || step === "idle") && (
          <div className="camera-stage">
            <video
              ref={videoRef}
              playsInline
              muted
              className={`camera-video ${facingMode === "user" ? "camera-video-mirrored" : ""}`}
              data-testid="camera-video"
            />
            <button
              className="camera-snap-btn"
              onClick={snap}
              disabled={step !== "live"}
              data-testid="camera-snap-btn"
              aria-label="Take photo"
            >
              <span className="camera-snap-inner" />
            </button>
            <button
              className="camera-flip-btn"
              onClick={() => setFacingMode((m) => (m === "user" ? "environment" : "user"))}
              disabled={step !== "live"}
              data-testid="camera-flip-btn"
              aria-label="Flip camera"
              title="Flip camera"
            >
              🔄
            </button>
          </div>
        )}

        {(step === "review" || step === "sending" || step === "done") && snapshot && (
          <div className="camera-review">
            <img src={snapshot} alt="Snapshot" className="camera-snapshot" data-testid="camera-snapshot" />

            {step !== "done" && (
              <input
                type="text"
                className="camera-caption-input"
                placeholder="Say something with it (optional)..."
                value={context}
                onChange={(e) => setContext(e.target.value)}
                disabled={step === "sending"}
                data-testid="camera-caption-input"
              />
            )}

            {reaction && (
              <div className="camera-reaction" data-testid="camera-reaction">
                <strong>Aura:</strong> {reaction}
              </div>
            )}

            <div className="camera-actions">
              {step === "review" && (
                <>
                  <button className="ghost-btn" onClick={retake} data-testid="camera-retake-btn">
                    ↻ Retake
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => setEditing(true)}
                    data-testid="camera-edit-btn"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="camera-share-btn"
                    onClick={() => setSharing(true)}
                    data-testid="camera-share-btn"
                    title="Share this photo"
                  >
                    ⋯ Share
                  </button>
                  <button className="init-btn" onClick={send} data-testid="camera-send-btn">
                    Send to Aura
                  </button>
                  {onPaintWallpaper && (
                    <button
                      className="init-btn camera-paint-btn"
                      onClick={() => {
                        if (paintWallpaperLocked) {
                          onPaintWallpaper({ locked: true });
                          return;
                        }
                        // Convert snapshot dataURL → File, hand off to parent.
                        const base64 = snapshot.split(",")[1];
                        const byteString = atob(base64);
                        const bytes = new Uint8Array(byteString.length);
                        for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
                        const file = new File([bytes], `aura-snap-${Date.now()}.jpg`, { type: "image/jpeg" });
                        onPaintWallpaper({ file });
                      }}
                      data-testid="camera-paint-wallpaper-btn"
                      title={paintWallpaperLocked ? "Premium feature" : "Paint me into a wallpaper"}
                    >
                      🎨 Paint into wallpaper {paintWallpaperLocked && <span aria-hidden> 🔒</span>}
                    </button>
                  )}
                </>
              )}
              {step === "sending" && (
                <button className="init-btn" disabled data-testid="camera-sending-btn">
                  ✨ Aura is looking...
                </button>
              )}
              {step === "done" && (
                <button className="init-btn" onClick={onClose} data-testid="camera-done-btn">
                  Done
                </button>
              )}
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
      {editing && snapshot && (
        <PhotoEditModal
          src={snapshot}
          onCancel={() => setEditing(false)}
          onApply={(editedB64) => {
            setSnapshot(`data:image/jpeg;base64,${editedB64}`);
            setEditing(false);
          }}
        />
      )}
      {sharing && snapshot && (
        <PhotoShareSheet
          imageBase64={snapshot.split(",")[1]}
          defaultCaption={context}
          onClose={() => setSharing(false)}
          onShareWithAura={async (cap) => {
            // Use the same /vision/analyze path as "Send to Aura" so chat reflows
            const base64 = snapshot.split(",")[1];
            const res = await axios.post(`${API}/vision/analyze`, {
              image_base64: base64,
              context: (cap || context).trim() || null,
            });
            if (onMoodUpdate && res.data?.mood) {
              onMoodUpdate({ mood: res.data.mood, emoji: res.data.mood_emoji });
            }
            if (onCaptured) {
              onCaptured({
                image_base64: base64,
                reaction: res.data?.reaction,
                mood: res.data?.mood,
                mood_emoji: res.data?.mood_emoji,
                mood_shifted: res.data?.mood_shifted,
              });
            }
            setReaction(res.data?.reaction || "");
            setStep("done");
            return { photo_id: res.data?.photo_id };
          }}
          onComplete={() => {
            // If user did NOT share with Aura, just close the camera modal
            // (the photo was either saved/shared elsewhere or only externally)
            setSharing(false);
            if (step === "review") {
              // Give a beat then close camera modal so user sees confirmation
              setTimeout(() => onClose && onClose(), 200);
            }
          }}
        />
      )}
    </div>
  );
}
